import { config } from "../config";
import {
  CORRELATION_ID_HEADER,
  getCorrelationId,
} from "../utils/getCorrelationId";
import { HttpError, isHttpError, toHttpError } from "./httpError";
import { reportClientEvent } from "./reportClientEvent";

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

export type HttpClientOptions = {
  authorisation?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  baseUrl?: string;
  // Supply one when the caller has to show the payer the same reference the
  // request carried (D-17). Left unset, the seam mints one per request.
  correlationId?: string;
};

const buildQuery = (query?: string | QueryParams): string => {
  if (!query) return "";
  if (typeof query === "string") return query;

  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      search.append(key, String(value));
    }
  });

  const serialised = search.toString();
  return serialised ? `?${serialised}` : "";
};

// Lifts a hand-written correlation header out of the caller's own headers,
// whatever casing they reached for. Without this a caller who wrote
// `x-correlation-id` would send two references and the backend would log one
// while support quoted the other.
const takeSuppliedCorrelationId = (
  headers: Record<string, string>
): string | undefined => {
  const key = Object.keys(headers).find(
    name => name.toLowerCase() === CORRELATION_ID_HEADER.toLowerCase()
  );

  if (!key) return undefined;

  const value = headers[key];
  delete headers[key];
  return value;
};

// The one place a request's headers are assembled, which is what makes the
// correlation ID unskippable (D-16) — the same argument that already keeps
// Authorization here.
const buildHeaders = (options: HttpClientOptions, accept: string) => {
  const supplied = { ...options.headers };
  const correlationId =
    options.correlationId ??
    takeSuppliedCorrelationId(supplied) ??
    getCorrelationId();

  const headers: Record<string, string> = {
    Accept: accept,
    "Content-Type": "application/json",
    ...supplied,
    [CORRELATION_ID_HEADER]: correlationId,
  };

  return { headers, correlationId };
};

const readJson = async <T>(
  response: Response,
  url: string,
  correlationId?: string
): Promise<T> => {
  const raw = await response.text();
  if (!raw) return undefined as T;

  try {
    return JSON.parse(raw) as T;
  } catch {
    throw HttpError.parse(url, raw, correlationId);
  }
};

// The single place that owns the status check. Every response goes through
// here, so no call site can opt out of it -- and none can opt out of the
// telemetry emit either, which is why the emit is here rather than in a hook
// or a component handler.
//
// The URL arrives as a thunk so that a config failure, raised while resolving
// it, is raised inside this try rather than before the call. Otherwise the one
// kind that never reaches fetch would be the one kind never reported.
const request = async <T>(
  resolve: () => string,
  init: RequestInit,
  parse: "json" | "blob" | "namedBlob",
  correlationId?: string
): Promise<T> => {
  try {
    const url = resolve();
    let response: Response;

    try {
      response = await fetch(url, init);
    } catch (cause) {
      throw HttpError.network(url, cause, correlationId);
    }

    if (!response.ok) {
      throw await toHttpError(response, url, correlationId);
    }

    if (response.status === 204) return undefined as T;

    if (parse === "namedBlob") {
      const blob = await response.blob();
      const filename = filenameFromDisposition(
        response.headers.get("Content-Disposition")
      );
      return { blob, filename } as T;
    }

    return parse === "blob"
      ? ((await response.blob()) as T)
      : await readJson<T>(response, url, correlationId);
  } catch (error) {
    // Observes only: the same value is rethrown at the same point. Not awaited,
    // so the sink cannot delay the failure the payer is waiting on.
    if (isHttpError(error)) reportClientEvent(error);

    throw error;
  }
};

// Undefined when the header is absent, unreadable or names no file, e.g. when
// CORS does not expose it. Only the plain `filename=` form is read.
export const filenameFromDisposition = (
  header: string | null
): string | undefined => {
  const name = header?.match(/(?:^|;)\s*filename="?([^";]+)"?/i)?.[1].trim();
  return name || undefined;
};

const resolveUrl = (
  endpoint: string,
  options: HttpClientOptions,
  query?: string | QueryParams,
  correlationId?: string
): string => {
  const baseUrl = options.baseUrl ?? config.REACT_APP_PROXY_BASE_URL;

  if (!baseUrl) {
    throw HttpError.config(
      endpoint,
      "REACT_APP_PROXY_BASE_URL is not configured",
      correlationId
    );
  }

  return `${baseUrl}/${endpoint}${buildQuery(query)}`;
};

export default function httpClient(
  endpoint: string,
  options: HttpClientOptions = {}
) {
  return {
    get: <T>(query?: string | QueryParams): Promise<T> => {
      const { headers, correlationId } = buildHeaders(
        options,
        "application/json"
      );

      return request<T>(
        () => resolveUrl(endpoint, options, query, correlationId),
        { method: "GET", headers, signal: options.signal },
        "json",
        correlationId
      );
    },

    getNamedBlob: (
      query?: string | QueryParams
    ): Promise<{ blob: Blob; filename?: string }> => {
      const { headers, correlationId } = buildHeaders(
        options,
        "application/json"
      );

      return request<{ blob: Blob; filename?: string }>(
        () => resolveUrl(endpoint, options, query, correlationId),
        { method: "GET", headers, signal: options.signal },
        "namedBlob",
        correlationId
      );
    },

    getBlob: (query?: string | QueryParams): Promise<Blob> => {
      const { headers, correlationId } = buildHeaders(
        options,
        "application/json"
      );

      return request<Blob>(
        () => resolveUrl(endpoint, options, query, correlationId),
        { method: "GET", headers, signal: options.signal },
        "blob",
        correlationId
      );
    },

    // Takes a query as well as a body: /schoolfee/payment is a POST whose
    // tenant selector is a query parameter, and building that URL at the call
    // site would put URL construction back outside the seam.
    post: <T>(body?: unknown, query?: string | QueryParams): Promise<T> => {
      const { headers, correlationId } = buildHeaders(options, "*/*");

      return request<T>(
        () => resolveUrl(endpoint, options, query, correlationId),
        {
          method: "POST",
          headers,
          signal: options.signal,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        },
        "json",
        correlationId
      );
    },
  };
}
